// Inventor-style measurement resolution: given two classified picks,
// choose the meaningful primary distance/angle and a panel of related values.
import * as THREE from 'three';
import { acuteAngleDeg, closestPointsOnAxes, pointPlaneDistance, type Vec3Tuple } from './math';
import { closestPointsOnSegments } from './geometry';
import type { DistanceMode, MeasureResult, MeasureSegment, MeasureValue, Pick } from './types';

function v3(t: Vec3Tuple): THREE.Vector3 {
  return new THREE.Vector3(t[0], t[1], t[2]);
}

function tup(p: THREE.Vector3): Vec3Tuple {
  return [p.x, p.y, p.z];
}

function kindLabel(k: Pick['kind']): string {
  if (k === 'cylinder') return 'Cylinder';
  if (k === 'plane') return 'Plane';
  if (k === 'edge') return 'Edge';
  if (k === 'vertex') return 'Vertex';
  return 'Freeform';
}

function deltaValues(a: THREE.Vector3, b: THREE.Vector3): MeasureValue[] {
  const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
  return [
    { id: 'dx', label: 'ΔX', value: Math.abs(dx), unit: 'mm' },
    { id: 'dy', label: 'ΔY', value: Math.abs(dy), unit: 'mm' },
    { id: 'dz', label: 'ΔZ', value: Math.abs(dz), unit: 'mm' },
    { id: 'xyz', label: '3D distance', value: Math.hypot(dx, dy, dz), unit: 'mm' }
  ];
}

function segment(a: THREE.Vector3, b: THREE.Vector3): MeasureSegment {
  return {
    a: a.clone(),
    b: b.clone(),
    mid: a.clone().add(b).multiplyScalar(0.5),
    distance: a.distanceTo(b)
  };
}

function cylinderAxisHalfLen(pick: Pick): number {
  return Math.max((pick.radius ?? 1) * 1.6, 0.5);
}

function isTopology(p: Pick): boolean {
  return p.kind === 'edge' || p.kind === 'vertex';
}

function edgeEndpoints(p: Pick): [THREE.Vector3, THREE.Vector3] {
  return [p.edgeA!.clone(), p.edgeB!.clone()];
}

function resolvePlanePlane(a: Pick, b: Pick): MeasureResult {
  const nA = a.normal.clone().normalize();
  const nB = b.normal.clone().normalize();
  const angleDeg = acuteAngleDeg(tup(nA), tup(nB));
  const isParallel = angleDeg < 0.5;

  // Perpendicular segment between the two planes, placed at the lateral
  // midpoint of the pick points (CAD-style — not a slanted point-to-point).
  const n = nA.clone();
  const mid = a.point.clone().add(b.point).multiplyScalar(0.5);
  const footA = mid.clone().sub(n.clone().multiplyScalar(mid.clone().sub(a.point).dot(n)));
  const footB = mid.clone().sub(n.clone().multiplyScalar(mid.clone().sub(b.point).dot(n)));
  const perp = footA.distanceTo(footB);
  const seg = segment(footA, footB);

  const values: MeasureValue[] = [
    { id: 'perp', label: isParallel ? 'Perpendicular distance' : 'Distance (along normal A)', value: perp, unit: 'mm' },
    { id: 'angle', label: 'Angle', value: angleDeg, unit: 'deg' },
    // ΔX/Y/Z of the pick points (lateral offset), not of the short perp segment.
    ...deltaValues(a.point, b.point)
  ];

  return {
    title: 'Plane → Plane',
    values,
    distanceModes: [],
    defaultMode: 'center',
    segments: { center: seg },
    angleDeg,
    isParallel,
    showAngle: !isParallel
  };
}

function resolvePlaneCylinder(plane: Pick, cyl: Pick): MeasureResult {
  const n = plane.normal.clone().normalize();
  const center = cyl.point.clone();
  const axis = (cyl.axisBasis?.axis ?? cyl.normal).clone().normalize();
  const r = cyl.radius ?? 0;

  const projectToPlane = (p: THREE.Vector3): THREE.Vector3 => {
    const d = p.clone().sub(plane.point).dot(n);
    return p.clone().sub(n.clone().multiplyScalar(d));
  };

  const centerDist = pointPlaneDistance(tup(center), tup(plane.point), tup(n));
  const foot = projectToPlane(center);

  const axisAngle = acuteAngleDeg(tup(axis), tup(n));
  const axisPlaneAngle = Math.abs(90 - axisAngle);

  const segCenter = segment(center, foot);
  const near = center.clone().addScaledVector(n, -r);
  const far = center.clone().addScaledVector(n, r);
  const nearDist = pointPlaneDistance(tup(near), tup(plane.point), tup(n));
  const farDist = pointPlaneDistance(tup(far), tup(plane.point), tup(n));
  const footNear = projectToPlane(near);
  const footFar = projectToPlane(far);

  const values: MeasureValue[] = [
    { id: 'center', label: 'Center → plane', value: centerDist, unit: 'mm' },
    { id: 'min', label: 'Min (wall → plane)', value: Math.min(nearDist, farDist), unit: 'mm' },
    { id: 'max', label: 'Max (wall → plane)', value: Math.max(nearDist, farDist), unit: 'mm' },
    { id: 'diam', label: 'Diameter', value: r * 2, unit: 'mm' },
    { id: 'radius', label: 'Radius', value: r, unit: 'mm' },
    { id: 'axisPlane', label: 'Axis–plane angle', value: axisPlaneAngle, unit: 'deg' },
    ...deltaValues(center, foot)
  ];

  return {
    title: 'Plane → Cylinder',
    values,
    distanceModes: ['min', 'center', 'max'],
    defaultMode: 'center',
    segments: {
      center: segCenter,
      min: nearDist <= farDist ? segment(near, footNear) : segment(far, footFar),
      max: nearDist >= farDist ? segment(near, footNear) : segment(far, footFar)
    },
    angleDeg: axisPlaneAngle,
    isParallel: axisPlaneAngle < 0.5 || Math.abs(axisPlaneAngle - 90) < 0.5,
    showAngle: axisPlaneAngle >= 0.5 && Math.abs(axisPlaneAngle - 90) >= 0.5,
    axisA: { origin: center.clone(), dir: axis, halfLen: cylinderAxisHalfLen(cyl) }
  };
}

function resolveCylinderCylinder(a: Pick, b: Pick): MeasureResult {
  const c1 = a.point.clone();
  const c2 = b.point.clone();
  const d1 = (a.axisBasis?.axis ?? a.normal).clone().normalize();
  const d2 = (b.axisBasis?.axis ?? b.normal).clone().normalize();
  const r1 = a.radius ?? 0;
  const r2 = b.radius ?? 0;

  const aa = closestPointsOnAxes(tup(c1), tup(d1), tup(c2), tup(d2));
  const p1 = v3(aa.closest1);
  const p2 = v3(aa.closest2);
  const centerDist = aa.distance;
  const angleDeg = acuteAngleDeg(tup(d1), tup(d2));
  const isParallel = aa.parallel || angleDeg < 0.5;

  const minDist = Math.max(0, centerDist - r1 - r2);
  const maxDist = centerDist + r1 + r2;

  let n = new THREE.Vector3().subVectors(p2, p1);
  if (n.lengthSq() < 1e-14) {
    n = new THREE.Vector3().crossVectors(d1, new THREE.Vector3(0, 0, 1));
    if (n.lengthSq() < 1e-14) n = new THREE.Vector3().crossVectors(d1, new THREE.Vector3(0, 1, 0));
  }
  n.normalize();
  const minA = p1.clone().addScaledVector(n, r1);
  const minB = p2.clone().addScaledVector(n, -r2);
  const maxA = p1.clone().addScaledVector(n, -r1);
  const maxB = p2.clone().addScaledVector(n, r2);

  const values: MeasureValue[] = [
    { id: 'center', label: 'Center-to-center', value: centerDist, unit: 'mm' },
    { id: 'min', label: 'Minimum distance', value: minDist, unit: 'mm' },
    { id: 'max', label: 'Maximum distance', value: maxDist, unit: 'mm' },
    { id: 'angle', label: 'Axis angle', value: angleDeg, unit: 'deg' },
    { id: 'diamA', label: 'Diameter A', value: r1 * 2, unit: 'mm' },
    { id: 'diamB', label: 'Diameter B', value: r2 * 2, unit: 'mm' },
    ...deltaValues(p1, p2)
  ];

  return {
    title: 'Cylinder → Cylinder',
    values,
    distanceModes: ['min', 'center', 'max'],
    defaultMode: 'center',
    segments: {
      center: segment(p1, p2),
      min: segment(minA, minB),
      max: segment(maxA, maxB)
    },
    angleDeg,
    isParallel,
    showAngle: !isParallel,
    axisA: { origin: c1, dir: d1, halfLen: cylinderAxisHalfLen(a) },
    axisB: { origin: c2, dir: d2, halfLen: cylinderAxisHalfLen(b) }
  };
}

function resolveVertexVertex(a: Pick, b: Pick): MeasureResult {
  const seg = segment(a.point, b.point);
  return {
    title: 'Vertex → Vertex',
    values: [
      { id: 'xyz', label: '3D distance', value: seg.distance, unit: 'mm' },
      ...deltaValues(a.point, b.point).filter(v => v.id !== 'xyz')
    ],
    distanceModes: [],
    defaultMode: 'center',
    segments: { center: seg },
    angleDeg: 0,
    isParallel: true,
    showAngle: false
  };
}

function resolvePointPlane(point: THREE.Vector3, plane: Pick, title: string): MeasureResult {
  const n = plane.normal.clone().normalize();
  const dist = pointPlaneDistance(tup(point), tup(plane.point), tup(n));
  const sign = Math.sign(new THREE.Vector3().subVectors(point, plane.point).dot(n)) || 1;
  const foot = point.clone().sub(n.clone().multiplyScalar(sign * dist));
  const seg = segment(point, foot);
  return {
    title,
    values: [
      { id: 'perp', label: 'Perpendicular distance', value: dist, unit: 'mm' },
      ...deltaValues(seg.a, seg.b)
    ],
    distanceModes: [],
    defaultMode: 'center',
    segments: { center: seg },
    angleDeg: 0,
    isParallel: true,
    showAngle: false
  };
}

function resolvePointCylinder(point: THREE.Vector3, cyl: Pick, title: string): MeasureResult {
  const axis = (cyl.axisBasis?.axis ?? cyl.normal).clone().normalize();
  const r = cyl.radius ?? 0;
  const w = new THREE.Vector3().subVectors(point, cyl.point);
  const axisDist = w.clone().cross(axis).length();
  const foot = cyl.point.clone().add(axis.clone().multiplyScalar(w.dot(axis)));
  const wallDist = Math.abs(axisDist - r);
  const seg = segment(point, foot);
  return {
    title,
    values: [
      { id: 'center', label: 'Distance to axis', value: axisDist, unit: 'mm' },
      { id: 'min', label: 'Distance to wall', value: wallDist, unit: 'mm' },
      { id: 'diam', label: 'Diameter', value: r * 2, unit: 'mm' },
      ...deltaValues(seg.a, seg.b)
    ],
    distanceModes: [],
    defaultMode: 'center',
    segments: { center: seg },
    angleDeg: 0,
    isParallel: true,
    showAngle: false,
    axisA: { origin: cyl.point.clone(), dir: axis, halfLen: cylinderAxisHalfLen(cyl) }
  };
}

function resolvePointEdge(point: THREE.Vector3, edge: Pick, title: string): MeasureResult {
  const [a0, a1] = edgeEndpoints(edge);
  const { dist, pa, pb } = closestPointsOnSegments(point, point, a0, a1);
  const seg = segment(pa, pb);
  return {
    title,
    values: [
      { id: 'xyz', label: '3D distance', value: dist, unit: 'mm' },
      ...deltaValues(pa, pb).filter(v => v.id !== 'xyz')
    ],
    distanceModes: [],
    defaultMode: 'center',
    segments: { center: seg },
    angleDeg: 0,
    isParallel: true,
    showAngle: false
  };
}

function resolveEdgeEdge(a: Pick, b: Pick): MeasureResult {
  const [a0, a1] = edgeEndpoints(a);
  const [b0, b1] = edgeEndpoints(b);
  const { dist, pa, pb } = closestPointsOnSegments(a0, a1, b0, b1);
  const seg = segment(pa, pb);
  const dirA = a1.clone().sub(a0).normalize();
  const dirB = b1.clone().sub(b0).normalize();
  const angleDeg = acuteAngleDeg(tup(dirA), tup(dirB));
  const isParallel = angleDeg < 0.5;
  return {
    title: 'Edge → Edge',
    values: [
      { id: 'xyz', label: 'Minimum distance', value: dist, unit: 'mm' },
      { id: 'angle', label: 'Angle', value: angleDeg, unit: 'deg' },
      ...deltaValues(pa, pb).filter(v => v.id !== 'xyz')
    ],
    distanceModes: [],
    defaultMode: 'center',
    segments: { center: seg },
    angleDeg,
    isParallel,
    showAngle: !isParallel
  };
}

function resolveEdgePlane(edge: Pick, plane: Pick): MeasureResult {
  const n = plane.normal.clone().normalize();
  const [a0, a1] = edgeEndpoints(edge);
  const d0 = pointPlaneDistance(tup(a0), tup(plane.point), tup(n));
  const d1 = pointPlaneDistance(tup(a1), tup(plane.point), tup(n));
  // Closest point on the finite segment to the plane.
  let point: THREE.Vector3;
  let dist: number;
  if (d0 <= d1) { point = a0; dist = d0; }
  else { point = a1; dist = d1; }
  // If the segment straddles the plane, minimum is zero at the intersection.
  const s0 = Math.sign(new THREE.Vector3().subVectors(a0, plane.point).dot(n)) || 1;
  const s1 = Math.sign(new THREE.Vector3().subVectors(a1, plane.point).dot(n)) || 1;
  if (s0 !== s1 && (d0 + d1) > 1e-9) {
    const t = d0 / (d0 + d1);
    point = a0.clone().lerp(a1, t);
    dist = 0;
  }
  const sign = Math.sign(new THREE.Vector3().subVectors(point, plane.point).dot(n)) || 1;
  const foot = point.clone().sub(n.clone().multiplyScalar(sign * dist));
  const seg = segment(point, foot);
  return {
    title: 'Edge → Plane',
    values: [
      { id: 'perp', label: 'Minimum distance', value: dist, unit: 'mm' },
      ...deltaValues(seg.a, seg.b)
    ],
    distanceModes: [],
    defaultMode: 'center',
    segments: { center: seg },
    angleDeg: 0,
    isParallel: true,
    showAngle: false
  };
}

function resolveEdgeCylinder(edge: Pick, cyl: Pick): MeasureResult {
  const axis = (cyl.axisBasis?.axis ?? cyl.normal).clone().normalize();
  const r = cyl.radius ?? 0;
  const axisHalf = Math.max(cylinderAxisHalfLen(cyl) * 4, getAxisSampleLen(cyl));
  const axisA = cyl.point.clone().addScaledVector(axis, -axisHalf);
  const axisB = cyl.point.clone().addScaledVector(axis, axisHalf);
  const [e0, e1] = edgeEndpoints(edge);
  const { dist: axisDist, pa, pb } = closestPointsOnSegments(e0, e1, axisA, axisB);
  const wallDist = Math.abs(axisDist - r);
  const seg = segment(pa, pb);
  return {
    title: 'Edge → Cylinder',
    values: [
      { id: 'center', label: 'Distance to axis', value: axisDist, unit: 'mm' },
      { id: 'min', label: 'Distance to wall', value: wallDist, unit: 'mm' },
      { id: 'diam', label: 'Diameter', value: r * 2, unit: 'mm' },
      ...deltaValues(pa, pb)
    ],
    distanceModes: [],
    defaultMode: 'center',
    segments: { center: seg },
    angleDeg: 0,
    isParallel: true,
    showAngle: false,
    axisA: { origin: cyl.point.clone(), dir: axis, halfLen: cylinderAxisHalfLen(cyl) }
  };
}

function getAxisSampleLen(cyl: Pick): number {
  return Math.max((cyl.radius ?? 1) * 20, 10);
}

function resolvePointPoint(a: THREE.Vector3, b: THREE.Vector3, title: string): MeasureResult {
  const seg = segment(a, b);
  return {
    title,
    values: [
      { id: 'xyz', label: '3D distance', value: seg.distance, unit: 'mm' },
      ...deltaValues(a, b).filter(v => v.id !== 'xyz')
    ],
    distanceModes: [],
    defaultMode: 'center',
    segments: { center: seg },
    angleDeg: 0,
    isParallel: true,
    showAngle: false
  };
}

function resolveTopology(a: Pick, b: Pick): MeasureResult {
  // Normalize order for mixed pairs so helpers stay simple.
  if (a.kind === 'vertex' && b.kind === 'vertex') return resolveVertexVertex(a, b);
  if (a.kind === 'edge' && b.kind === 'edge') return resolveEdgeEdge(a, b);

  if (a.kind === 'vertex' && b.kind === 'edge') return resolvePointEdge(a.point, b, 'Vertex → Edge');
  if (a.kind === 'edge' && b.kind === 'vertex') return resolvePointEdge(b.point, a, 'Vertex → Edge');

  if (a.kind === 'vertex' && b.kind === 'plane') return resolvePointPlane(a.point, b, 'Vertex → Plane');
  if (a.kind === 'plane' && b.kind === 'vertex') return resolvePointPlane(b.point, a, 'Vertex → Plane');

  if (a.kind === 'vertex' && b.kind === 'cylinder') return resolvePointCylinder(a.point, b, 'Vertex → Cylinder');
  if (a.kind === 'cylinder' && b.kind === 'vertex') return resolvePointCylinder(b.point, a, 'Vertex → Cylinder');

  if (a.kind === 'edge' && b.kind === 'plane') return resolveEdgePlane(a, b);
  if (a.kind === 'plane' && b.kind === 'edge') return resolveEdgePlane(b, a);

  if (a.kind === 'edge' && b.kind === 'cylinder') return resolveEdgeCylinder(a, b);
  if (a.kind === 'cylinder' && b.kind === 'edge') return resolveEdgeCylinder(b, a);

  if (a.kind === 'vertex' || b.kind === 'vertex') {
    const v = a.kind === 'vertex' ? a : b;
    const other = v === a ? b : a;
    return resolvePointPoint(v.point, other.point, `Vertex → ${kindLabel(other.kind)}`);
  }

  // edge + freeform (or similar): closest point on edge to other point
  const edge = a.kind === 'edge' ? a : b;
  const other = edge === a ? b : a;
  return resolvePointEdge(other.point, edge, `Edge → ${kindLabel(other.kind)}`);
}

function resolveFreeform(a: Pick, b: Pick): MeasureResult {
  const plane = a.kind === 'plane' ? a : b.kind === 'plane' ? b : null;
  const other = plane === a ? b : a;
  if (plane) {
    return resolvePointPlane(other.point, plane, `${kindLabel(a.kind)} → ${kindLabel(b.kind)}`);
  }

  if (a.kind === 'cylinder' || b.kind === 'cylinder') {
    const cyl = a.kind === 'cylinder' ? a : b;
    const pt = cyl === a ? b : a;
    return resolvePointCylinder(pt.point, cyl, `${kindLabel(a.kind)} → ${kindLabel(b.kind)}`);
  }

  // Nearly parallel hit-normals: treat as plane–plane (tessellation often
  // mislabels flat faces as freeform).
  const angleDeg = acuteAngleDeg(tup(a.normal), tup(b.normal));
  if (angleDeg < 2) {
    const asPlaneA: Pick = { ...a, kind: 'plane' };
    const asPlaneB: Pick = { ...b, kind: 'plane' };
    const result = resolvePlanePlane(asPlaneA, asPlaneB);
    return { ...result, title: 'Plane → Plane' };
  }

  const seg = segment(a.point, b.point);
  return {
    title: 'Freeform → Freeform',
    values: [
      { id: 'xyz', label: '3D distance', value: seg.distance, unit: 'mm' },
      ...deltaValues(a.point, b.point).filter(v => v.id !== 'xyz')
    ],
    distanceModes: [],
    defaultMode: 'center',
    segments: { center: seg },
    angleDeg: 0,
    isParallel: true,
    showAngle: false
  };
}

/** Resolve a two-pick measurement into panel values + graphics segments. */
export function resolveMeasurement(a: Pick, b: Pick): MeasureResult {
  if (isTopology(a) || isTopology(b)) return resolveTopology(a, b);
  if (a.kind === 'plane' && b.kind === 'plane') return resolvePlanePlane(a, b);
  if (a.kind === 'plane' && b.kind === 'cylinder') return resolvePlaneCylinder(a, b);
  if (a.kind === 'cylinder' && b.kind === 'plane') return resolvePlaneCylinder(b, a);
  if (a.kind === 'cylinder' && b.kind === 'cylinder') return resolveCylinderCylinder(a, b);
  return resolveFreeform(a, b);
}

export function segmentForMode(result: MeasureResult, mode: DistanceMode): MeasureSegment {
  return result.segments[mode] ?? result.segments.center ?? result.segments.min ?? {
    a: new THREE.Vector3(),
    b: new THREE.Vector3(),
    mid: new THREE.Vector3(),
    distance: 0
  };
}
